import { DownOutlined, ReloadOutlined, UpOutlined } from '@ant-design/icons';
import { ConfigProvider, Select, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { Link } from 'react-router-dom';
import { SessionYearMonthSelect } from '@components';
import { COLOR_TOOLTIP, DATA_EMPTY } from '@constants/common';
import { TProgressRow, PROGRESS_REVIEW_CONSTANT } from './constant';
import useProgressReviewPage from './hooks/useProgressReviewPage';
import { ProgressReviewWrapper } from './ProgressReviewPageStyled';

const { TITLE, LABEL } = PROGRESS_REVIEW_CONSTANT;

// Same 3-state cycle as VAMS_PC_004 (FixedAssetLedger/columns.tsx): ascend → descend → cancel.
// Sort itself stays client-side (comparator below) — no extra network round-trip on header click.
const sortDirections: SortOrder[] = ['ascend', 'descend', null];

const numericSorter =
  (key: keyof TProgressRow) => (a: TProgressRow, b: TProgressRow) =>
    (a[key] as number) - (b[key] as number);

// ── Ellipsis + hover tooltip, same pattern as 資産名称 in InventoryReportPage ───
function EllipsisTooltipCell({ value }: { value?: string | null }) {
  if (!value) {
    return <span>{DATA_EMPTY}</span>;
  }
  return (
    <span className="progress-review_ellipsis">
      <Tooltip
        color="white"
        overlayClassName="common_custom-tooltip"
        title={value}
        overlayInnerStyle={{ color: COLOR_TOOLTIP }}
      >
        {value}
      </Tooltip>
    </span>
  );
}

// ── Store link cell [4.2] ──────────────────────────────────────────────────────
function StoreLinkCell({ row }: { row: TProgressRow }) {
  if (!row.storeDisplayText || !row.ledgerLink) {
    return <span>-</span>;
  }
  return (
    <span className="progress-review_ellipsis">
      <Tooltip
        color="white"
        overlayClassName="common_custom-tooltip"
        title={row.storeDisplayText}
        overlayInnerStyle={{ color: COLOR_TOOLTIP }}
      >
        <Link to={row.ledgerLink} className="progress-review_store-link">
          {row.storeDisplayText}
        </Link>
      </Tooltip>
    </span>
  );
}

// ── Department link cell [4.10] — blank on store rows; shown on expanded department rows ──────
function DepartmentLinkCell({ row }: { row: TProgressRow }) {
  if (!row.departmentCode || !row.ledgerLink) {
    return <span>-</span>;
  }
  const displayText = row.departmentName || row.departmentCode;
  return (
    <span className="progress-review_ellipsis">
      <Tooltip
        color="white"
        overlayClassName="common_custom-tooltip"
        title={displayText}
        overlayInnerStyle={{ color: COLOR_TOOLTIP }}
      >
        <Link to={row.ledgerLink} className="progress-review_store-link">
          {displayText}
        </Link>
      </Tooltip>
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function ProgressReviewPage() {
  const { state, handler } = useProgressReviewPage();
  const {
    yearMonth,
    companyCode,
    storeCode,
    yearMonthOptions,
    companyOptions,
    storeOptions,
    isStoreDisabled,
    displayRows,
    totalRow,
    loading,
  } = state;

  const columns: ColumnsType<TProgressRow> = [
    // Dedicated placeholder column so the expand icon gets its own <td> instead of
    // sharing/wrapping inside 会社's cell (antd merges the icon into expandIconColumnIndex's cell).
    {
      title: '',
      key: 'expandPlaceholder',
      width: 40,
      render: () => null,
    },
    {
      title: LABEL.COMPANY_COL,
      dataIndex: 'companyCode',
      key: 'companyCode',
      width: 260,
      render: (_: unknown, row: TProgressRow) =>
        // Department (child) row — 会社 stays blank, not "-" (see [4.10] 部門 spec).
        row.departmentCode ? null : (
          <EllipsisTooltipCell
            value={
              row.companyCode && row.companyName
                ? `${row.companyCode}_${row.companyName}`
                : row.companyCode
            }
          />
        ),
    },
    {
      title: LABEL.STORE_COL,
      dataIndex: 'storeDisplayText',
      key: 'storeDisplayText',
      width: 260,
      render: (_: unknown, row: TProgressRow) =>
        // Department (child) row — 店舗 stays blank, not "-" (see [4.10] 部門 spec).
        row.departmentCode ? null : <StoreLinkCell row={row} />,
    },
    {
      title: LABEL.DEPARTMENT_COL,
      dataIndex: 'departmentCode',
      key: 'departmentCode',
      width: 200,
      render: (_: unknown, row: TProgressRow) => (
        <DepartmentLinkCell row={row} />
      ),
    },
    {
      title: LABEL.START_DATE_COL,
      dataIndex: 'inventoryStartDateDisplay',
      key: 'inventoryStartDateDisplay',
      width: 90,
      align: 'center',
      render: (val: string) => val || '-',
    },
    {
      title: LABEL.TOTAL_COL,
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('totalCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: LABEL.CONFIRMED_COL,
      dataIndex: 'confirmedCount',
      key: 'confirmedCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('confirmedCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: LABEL.RATE_COL,
      dataIndex: 'confirmedRateDisplay',
      key: 'confirmedRate',
      width: 50,
      align: 'right',
      sorter: numericSorter('confirmedRate'),
      sortDirections,
      // VAL-003: totalCount=0 → "-"
      render: (val: string, row: TProgressRow) =>
        row.totalCount === 0 ? '-' : val,
    },
    {
      title: LABEL.UNCONFIRMED_COL,
      dataIndex: 'unconfirmedCount',
      key: 'unconfirmedCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('unconfirmedCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: LABEL.UNUSED_COL,
      dataIndex: 'unusedCount',
      key: 'unusedCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('unusedCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: LABEL.UNLISTED_COL,
      dataIndex: 'unlistedCount',
      key: 'unlistedCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('unlistedCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: LABEL.MOVE_COL,
      dataIndex: 'moveCount',
      key: 'moveCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('moveCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: LABEL.REMOVE_COL,
      dataIndex: 'removeCount',
      key: 'removeCount',
      width: 60,
      align: 'right',
      sorter: numericSorter('removeCount'),
      sortDirections,
      render: (val: number) => val.toLocaleString(),
    },
  ];

  return (
    <ProgressReviewWrapper>
      {/* Page title */}
      <div className="progress-review_header">
        <h1>{TITLE.PAGE}</h1>
      </div>

      {/* [3] Filter Area */}
      <div className="progress-review_filter">
        {/* [3.1] 年月 */}
        <div className="progress-review_filter-item">
          <label>{LABEL.YEAR_MONTH}</label>
          <SessionYearMonthSelect
            sessions={yearMonthOptions.map((option) => ({
              inventoryYearMonth: option.value,
            }))}
            value={yearMonth}
            onChange={handler.handleChangeYearMonth}
          />
        </div>

        {/* [3.2] 会社 */}
        <div className="progress-review_filter-item">
          <label>{LABEL.COMPANY}</label>
          <Select
            size="large"
            value={companyCode}
            options={companyOptions}
            onChange={handler.handleChangeCompany}
            style={{ minWidth: 140 }}
          />
        </div>

        {/* [3.3] 店舗 — disabled when 会社="全て" (VAL-002) */}
        <div className="progress-review_filter-item">
          <label>{LABEL.STORE}</label>
          <Select
            size="large"
            value={storeCode || undefined}
            placeholder={LABEL.ALL_STORES}
            options={storeOptions}
            onChange={handler.handleChangeStore}
            disabled={isStoreDisabled}
            style={{ minWidth: 160 }}
            allowClear
          />
        </div>

        {/* [3.4] Refresh */}
        <button
          className="progress-review_refresh-btn"
          onClick={handler.handleRefresh}
          disabled={loading}
          title="更新"
          type="button"
        >
          {loading ? <Spin size="small" /> : <ReloadOutlined />}
        </button>
      </div>

      {/* [4] Progress Table / [6] No Data */}
      {displayRows.length === 0 && !loading ? (
        <div className="progress-review_no-data">{LABEL.NO_DATA}</div>
      ) : (
        <div className="progress-review_table-wrapper">
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: 'var(--background-header-table, #f0f4f8)',
                  headerColor: 'var(--color-header-table, #344054)',
                },
              },
            }}
          >
            <Table<TProgressRow>
              dataSource={displayRows}
              columns={columns}
              rowKey={(row) => row.key ?? `${row.companyCode}-${row.storeCode}`}
              pagination={false}
              showSorterTooltip={false}
              loading={loading}
              scroll={{ x: 'max-content' }}
              expandable={{
                childrenColumnName: 'departments',
                rowExpandable: (row) => (row.departments?.length ?? 0) > 0,
                expandIconColumnIndex: 0,
                expandIcon: ({ expanded, onExpand, record }) =>
                  (record.departments?.length ?? 0) > 0 ? (
                    <button
                      className="progress-review_expand-icon-btn"
                      onClick={(e) => onExpand(record, e)}
                      type="button"
                    >
                      {expanded ? <UpOutlined /> : <DownOutlined />}
                    </button>
                  ) : null,
              }}
              summary={() =>
                totalRow ? (
                  <Table.Summary.Row className="progress-review_total-row">
                    <Table.Summary.Cell index={0} />
                    <Table.Summary.Cell index={1}>
                      <strong>{LABEL.TOTAL_ROW_LABEL}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} align="right">
                      <strong>{totalRow.totalCount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <strong>
                        {totalRow.confirmedCount.toLocaleString()}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      <strong>{totalRow.confirmedRateDisplay}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      <strong>
                        {totalRow.unconfirmedCount.toLocaleString()}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">
                      <strong>{totalRow.unusedCount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">
                      <strong>{totalRow.unlistedCount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right">
                      <strong>{totalRow.moveCount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={12} align="right">
                      <strong>{totalRow.removeCount.toLocaleString()}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ) : null
              }
            />
          </ConfigProvider>
        </div>
      )}
    </ProgressReviewWrapper>
  );
}

export { ProgressReviewPage };
